// [전체 코드 미사용/주석 삭제 금지]

// Base imports
import React, { useState, useEffect, useCallback } from 'react';
import useModal from 'hooks/useModal';
import { API_BASE_URL } from 'config/apiConfig';       // API 호출용
import { getTokenStatus } from 'utils/tokenManager';   // 토큰 확인용

// CSS imports
import 'styles/Font.css';

// Image imports
import homeLogo from 'assets/images/logo/home-logo.svg';
import alarmIcon from 'assets/images/icon/alarm-icon.svg';
import gearIcon from 'assets/images/icon/gear-icon.svg';

// Component imports
import AlarmModal from "components/ui/Modals/AlarmModal/AlarmModal";

/**
 * 앱 전체에서 사용되는 공통 헤더 컴포넌트
 */
function AppHeader({
    type = 'title',
    title = '',
    hasSettingIcon = false,
    onSettingClick = () => {
    },
}) {

    const alarmModal = useModal();
    const [hasUnread, setHasUnread] = useState(false); // 읽지 않은 알림 상태

    // 읽지 않은 알림 체크 함수
    const checkUnreadStatus = useCallback(async () => {
        const { token } = getTokenStatus();
        if (!token) return;

        try {
            const response = await fetch(`${API_BASE_URL}/pushList`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                const list = data.list || [];

                // read 값이 'N'인 항목이 하나라도 있으면 true
                const hasNew = list.some(item => item.read === 'N');
                setHasUnread(hasNew);
            }
        } catch (error) {
            console.error("Alarm Check Error:", error);
        }
    }, []);

    // 컴포넌트 마운트 시 체크
    useEffect(() => {
        checkUnreadStatus();
    }, [checkUnreadStatus]);

    // 모달이 닫힐 때 재체크 (모달 내부에서 읽었을 수 있으므로)
    const handleModalClose = () => {
        alarmModal.close();
        checkUnreadStatus();
    };

    // 공통 Wrapper 스타일
    const wrapperStyle = type === 'home' ? headerStyles.homeHeaderField : headerStyles.titleHeaderField;

    const renderMainRow = () => (
        <div style={headerStyles.mainRow}>
            {/* 좌측 요소 */}
            <div style={headerStyles.leftElement}>
                {type === 'home' ? (
                    <img className="homeHeaderLogo" src={homeLogo} alt="Logo" style={headerStyles.homeHeaderLogo} />
                ) : (
                    <span className="titleHeaderText" style={headerStyles.titleHeaderText}>
                        {title}
                    </span>
                )}
            </div>

            {/* 우측 아이콘 컨테이너 */}
            <div style={headerStyles.headerIconContainer}>
                {/* [수정] 알람 아이콘 래퍼 (뱃지 위치 잡기 위해 div로 감쌈) */}
                <div style={headerStyles.iconWrapper} onClick={alarmModal.open}>
                    <img
                        className="headerAlarm"
                        src={alarmIcon}
                        alt="Alarm"
                        style={headerStyles.headerIcon}
                    />
                    {/* [추가] 읽지 않은 알림이 있으면 빨간 점 표시 */}
                    {hasUnread && <div style={headerStyles.redDot} />}
                </div>

                {hasSettingIcon && (
                    <img className="setting" src={gearIcon} alt="Setting" style={headerStyles.headerIcon}
                        onClick={onSettingClick} />
                )}
            </div>
        </div>
    );

    return (
        <>
            <header style={wrapperStyle}>
                {renderMainRow()}
            </header>

            <AlarmModal
                isOpen={alarmModal.isOpen}
                onClose={handleModalClose} // 닫힐 때 상태 재체크 연결
            />
        </>
    );
}

// [!! AppHeader 전용 스타일 !!]
/** @type {React.CSSProperties} */
const headerStyles = {
    // --- 1. Home 헤더 Wrapper 스타일 ---
    homeHeaderField: {
        width: '100%',
        color: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        boxSizing: 'border-box',

        position: 'sticky',
        top: 0,
        zIndex: 100, // zIndex는 유지
        backgroundColor: 'transparent', // 배경색이 투명했으므로 유지

        // 상단 패딩: 안전영역 + 12px
        // paddingTop: 'calc(env(safe-area-inset-top) + 12px)',
        paddingTop: 'max(env(safe-area-inset-top), 60px)',
        paddingLeft: '24px',
        paddingRight: '24px',
        paddingBottom: '12px',

        gap: '0px',
        marginBottom: '0px',
        // iOS 흔들림 방지 CSS는 sticky에서는 불필요하거나 충돌할 수 있으므로 제거하는 것이 좋습니다.
    },

    // --- 2. Title 헤더 Wrapper 스타일 (MyPage, BoostMain 공통) ---
    titleHeaderField: {
        width: '100%',
        flexShrink: 0, // 헤더
        boxSizing: 'border-box',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        backgroundColor: '#0D042D',
        // 상단 패딩 동일 적용
        // paddingTop: 'calc(env(safe-area-inset-top) + 12px)',
        paddingTop: 'max(env(safe-area-inset-top), 60px)',
        paddingLeft: '24px',
        paddingRight: '24px',
        paddingBottom: '12px',
    },

    // --- 3. 공통 메인 Row ---
    mainRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        height: '31px'
    },
    leftElement: {
        display: 'flex',
        alignItems: 'center',
        height: '100%',
    },

    // --- 4. Home Logo / Title Text ---
    homeHeaderLogo: { width: '144px', height: '13px', color: '#FFFFFF' },
    titleHeaderText: { // MyPage, BoostMain 공통
        fontFamily: 'Pretendard',
        fontWeight: 700,
        fontSize: '26px',
        color: '#FFFFFF',
        // 폰트 크기(26px)가 컨테이너(31px) 안에서 중앙에 오도록 라인 높이 조정
        lineHeight: '31px',
        display: 'block',
    },

    // --- 5. Icon Container ---
    headerIconContainer: { // MyPage, BoostMain, Home 공통
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        height: '100%', // 부모 높이에 맞춤
    },
    headerIcon: { // MyPage, BoostMain, Home 공통
        width: '24px',
        height: '24px',
        color: '#FCFCFA',
        cursor: 'pointer',
    },

    // ▼▼▼ [추가] 뱃지 관련 스타일 ▼▼▼
    iconWrapper: {
        position: 'relative', // 뱃지 절대 위치 기준점
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    redDot: {
        position: 'absolute',
        top: '0px',     // 아이콘 우상단 배치
        right: '0px',
        width: '6px',   // 뱃지 크기
        height: '6px',
        backgroundColor: '#FF5252', // 빨간색
        borderRadius: '50%',
        zIndex: 10,
        // 필요 시 테두리 추가 (배경색과 분리)
        // border: '1px solid #0D042D'
    }
};

export default AppHeader;